import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import { FiUsers, FiUserCheck, FiEdit2, FiPhone, FiMail, FiGlobe, FiMapPin, FiFileText, FiGrid, FiUser, FiChevronRight, FiX, FiPlus, FiSmartphone } from 'react-icons/fi';
import { FaAndroid, FaApple } from 'react-icons/fa';

const Account = () => {
  const { isAuthenticated, user, company } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('organization');
  const [selectedMember, setSelectedMember] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [gstinInput, setGstinInput] = useState('');
  const [panInput, setPanInput] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [websiteInput, setWebsiteInput] = useState('');
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [selectedDeptIdForQR, setSelectedDeptIdForQR] = useState('');
  const [downloadRole, setDownloadRole] = useState('field');
  const [downloadPlatform, setDownloadPlatform] = useState('android');

  // Color palette matching Attendance.js
  const colors = useMemo(() => ({
    // Neutralized palette (reduce excess colors)
    primary: '#111827',
    primaryBg: '#f3f4f6',
    success: '#111827',
    successBg: '#f3f4f6',
    warning: '#111827',
    warningBg: '#f3f4f6',
    purple: '#111827',
    purpleBg: '#f3f4f6',
    danger: '#b91c1c',
    dangerBg: '#fef2f2',
    border: '#e5e7eb',
    text: '#111827',
    textMuted: '#6b7280',
    cardBg: '#ffffff',
    pageBg: '#f8fafc'
  }), []);

  const getAvatarColor = (index) => {
    const colorList = ['#111827', '#1f2937', '#374151', '#4b5563', '#6b7280'];
    return colorList[index % colorList.length];
  };

  const isMemberActive = (member) => {
    if (!member) return false;

    if (member.status === 'active') return true;
    if (member.status && member.status !== 'active') return false;

    if (member.isActive === false) return false;

    // Employees are linked to companies via EmployeeCompany; use company-specific status.
    if (Array.isArray(member.companies) && member.companies.length > 0) {
      const currentCompanyId = Number(company?.id);
      if (!currentCompanyId) return false;

      return member.companies.some((cmp) => {
        if (Number(cmp?.id) !== currentCompanyId) return false;
        return cmp?.EmployeeCompany?.isActive === true;
      });
    }

    return member.isActive === true;
  };

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!company?.id) return;
      
      try {
        setLoading(true);
        const [departmentsRes, employeesRes, supervisorsRes, companyDetailsRes] = await Promise.all([
          api.get(`/api/departments/${company.id}`),
          api.get(`/api/companies/${company.id}/employees`),
          api.get('/api/companies/users'),
          api.get(`/api/companies/${company.id}`)
        ]);

        setDepartments(departmentsRes.data.departments || []);
        setEmployees(employeesRes.data.employees || []);
        setSupervisors(supervisorsRes.data.users || []);
        
        if (companyDetailsRes.data.is_success) {
          setCompanyDetails(companyDetailsRes.data.company);
        }
      } catch (err) {
        setError('Failed to load company data');
        console.error('Error fetching company data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [company?.id]);

  useEffect(() => {
    if (companyDetails) {
      setGstinInput(companyDetails.gstin || '');
      setPanInput(companyDetails.pan || '');
      setPhoneInput(companyDetails.phone || '');
      setWebsiteInput(companyDetails.website || '');
    }
  }, [companyDetails]);

  const handleMemberClick = (member, index) => {
    setSelectedMember({ ...member, index });
  };

  const formatAddress = (address) => {
    if (!address) return null;
    return typeof address === 'object' ? address.address : address;
  };

  const getOnboardingQrValue = () => {
    const dept = departments.find(d => String(d.id) === String(selectedDeptIdForQR));
    return JSON.stringify({
      companyId: company?.id,
      departmentId: dept?.id || null,
      departmentName: dept?.name || null
    });
  };
  
  const qrLogoSrc = companyDetails?.logo || company?.logo || '/logo.png';

  const getDownloadAppUrl = () => {
    if (downloadRole === 'field') {
      return downloadPlatform === 'android'
        ? 'https://play.google.com/store/apps/details?id=com.bloomnav.employee'
        : 'https://apps.apple.com/in/app/bloomnav-employee/id6749189070';
    }
    return downloadPlatform === 'android'
      ? 'https://play.google.com/store/apps/details?id=com.bloomnav.organisation'
      : 'https://apps.apple.com/app/id6745412517';
  };

  const handleUploadFile = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setSaving(true);
      try {
        const { data } = await api.get('/api/upload/presigned-url', { params: { fileType: file.type, dir: `logos/cmp-${company.id}` } });
        const { uploadUrl, fileUrl } = data;
        await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (type === 'logo') setLogoPreview(fileUrl);
        else setCoverPreview(fileUrl);
      } catch (e) { console.error(e); alert('Failed to upload'); }
      finally { setSaving(false); }
    };
    input.click();
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      const payload = { phone: phoneInput || null, website: websiteInput || null, gstin: gstinInput || null, pan: panInput || null };
      if (logoPreview) payload.logo = logoPreview;
      if (coverPreview) payload.coverImage = coverPreview;
      const resp = await api.put(`/api/companies/${company.id}`, payload);
      setCompanyDetails(resp.data.company);
      setIsEditOpen(false);
      setLogoPreview(null);
      setCoverPreview(null);
    } catch (e) { console.error(e); alert('Failed to save company info'); }
    finally { setSaving(false); }
  };

  // Member detail modal (matching Attendance.js style)
  const renderMemberModal = () => {
    if (!selectedMember) return null;

    const member = selectedMember;
    const isActive = isMemberActive(member);
    const initials = `${member.firstName?.charAt(0) || ''}${member.lastName?.charAt(0) || ''}`.toUpperCase() || '?';
    
    // Get departments for this member
    const memberDepts = member.departments || [];
    
    // Calculate supervised employees if supervisor
    const supervisedCount = member.isSupervisor ? employees.filter(emp => 
      emp.departments?.some(d => member.departments?.some(sd => sd.id === d.id))
    ).length : 0;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}
        onClick={() => setSelectedMember(null)}
      >
        <div
          style={{
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: 20,
            maxWidth: 480,
            width: '100%',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1)'
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header with gradient */}
          <div style={{
            background: `linear-gradient(135deg, ${getAvatarColor(selectedMember.index || 0)} 0%, ${getAvatarColor((selectedMember.index || 0) + 2)} 100%)`,
            padding: '24px 24px 48px',
            position: 'relative'
          }}>
            <button
              onClick={() => setSelectedMember(null)}
              className="btn btn--ghost btn--icon-only"
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                minWidth: 32,
                height: 32,
                minHeight: 32,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.18)'
              }}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: 'rgba(255,255,255,0.25)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontWeight: 500,
                fontSize: 22,
                border: '2px solid rgba(255,255,255,0.3)',
                overflow: 'hidden'
              }}>
                {member.profilePicture ? (
                  <img src={member.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : initials}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 500, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                  {member.firstName || 'Unknown'} {member.lastName || ''}
                </div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4 }}>
                  {member.email || 'No email'}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#fff',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '3px 8px',
                    borderRadius: 4
                  }}>
                    {member.isAdmin ? 'Admin' : member.isSupervisor ? 'Supervisor' : 'Field Staff'}
                  </span>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#fff',
                    background: isActive ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)',
                    padding: '3px 8px',
                    borderRadius: 4
                  }}>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards - only show for supervisors/admins */}
          {(member.isSupervisor || member.isAdmin) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: member.isSupervisor ? 'repeat(2, 1fr)' : '1fr',
              gap: 10,
              padding: '16px 20px',
              background: '#fff',
              borderBottom: `1px solid ${colors.border}`
            }}>
              <div style={{
                textAlign: 'center',
                padding: '10px 6px',
                background: colors.primaryBg,
                borderRadius: 8
              }}>
                <div style={{ fontSize: 18, fontWeight: 500, color: colors.primary }}>{memberDepts.length}</div>
                <div style={{ fontSize: 9, color: colors.textMuted, marginTop: 2 }}>Teams</div>
              </div>
              {member.isSupervisor && (
                <div style={{
                  textAlign: 'center',
                  padding: '10px 6px',
                  background: colors.purpleBg,
                  borderRadius: 8
                }}>
                  <div style={{ fontSize: 18, fontWeight: 500, color: colors.purple }}>{supervisedCount}</div>
                  <div style={{ fontSize: 9, color: colors.textMuted, marginTop: 2 }}>Supervising</div>
                </div>
              )}
            </div>
          )}

          {/* Details */}
          <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                Contact Information
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: colors.pageBg, padding: 12, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 10, color: colors.textMuted, marginBottom: 4 }}>Email</div>
                  <div style={{ fontSize: 13, color: colors.text, wordBreak: 'break-word' }}>{member.email || '-'}</div>
                </div>
                <div style={{ background: colors.pageBg, padding: 12, borderRadius: 8, border: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 10, color: colors.textMuted, marginBottom: 4 }}>Phone</div>
                  <div style={{ fontSize: 13, color: colors.text }}>{member.phone || '-'}</div>
                </div>
              </div>
            </div>

            {memberDepts.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Teams
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {memberDepts.map((dept, idx) => (
                    <span key={dept.id || idx} style={{
                      padding: '6px 12px',
                      background: colors.pageBg,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      fontSize: 12,
                      color: colors.text,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <FiUsers size={12} color={colors.textMuted} />
                      {dept.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Edit Company Modal
  const renderEditModal = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20
      }}
      onClick={() => setIsEditOpen(false)}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 520,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
          <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: colors.text }}>Edit Company Information</h3>
          <button
            onClick={() => setIsEditOpen(false)}
            className="btn btn--outline btn--icon-only"
            style={{ width: 32, minWidth: 32, height: 32, minHeight: 32, borderRadius: 999 }}
            aria-label="Close"
            title="Close"
          >
            <FiX size={16} />
          </button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>Phone</label>
              <input
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="Enter phone"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>Website</label>
              <input
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                value={websiteInput}
                onChange={(e) => setWebsiteInput(e.target.value)}
                placeholder="Enter website URL"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>GSTIN</label>
              <input
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                value={gstinInput}
                onChange={(e) => setGstinInput(e.target.value)}
                placeholder="Enter GSTIN"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>PAN</label>
              <input
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                value={panInput}
                onChange={(e) => setPanInput(e.target.value)}
                placeholder="Enter PAN"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(logoPreview || companyDetails?.logo) && (
                  <img src={logoPreview || companyDetails?.logo} alt="Logo" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', border: `1px solid ${colors.border}` }} />
                )}
                <button
                  onClick={() => handleUploadFile('logo')}
                  className="btn btn--outline btn--sm"
                >
                  Upload
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: colors.textMuted, marginBottom: 6 }}>Cover Image</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {(coverPreview || companyDetails?.coverImage) && (
                  <img src={coverPreview || companyDetails?.coverImage} alt="Cover" style={{ width: 80, height: 48, borderRadius: 8, objectFit: 'cover', border: `1px solid ${colors.border}` }} />
                )}
                <button
                  onClick={() => handleUploadFile('cover')}
                  className="btn btn--outline btn--sm"
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            onClick={() => setIsEditOpen(false)}
            className="btn btn--secondary btn--md"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveCompany}
            disabled={saving}
            className="btn btn--primary btn--md"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );

  // Invite User Modal
  const renderInviteModal = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20
      }}
      onClick={() => setIsAddUserOpen(false)}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          maxWidth: 640,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 25px 60px -15px rgba(0,0,0,0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 500, color: colors.text }}>Invite User</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.textMuted }}>Share QR codes with new team members</p>
          </div>
          <button
            onClick={() => setIsAddUserOpen(false)}
            className="btn btn--outline btn--icon-only"
            style={{ width: 32, minWidth: 32, height: 32, minHeight: 32, borderRadius: 999 }}
            aria-label="Close"
            title="Close"
          >
            <FiX size={16} />
          </button>
        </div>

        {departments.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: colors.textMuted }}>
            <FiUsers size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>No Teams Found</div>
            <div style={{ fontSize: 13 }}>Create a team first to generate onboarding QR codes.</div>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 24 }}>
            

            {/* Download App QR */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                Download App
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
                <button
                  onClick={() => setDownloadRole('field')}
                  className={`btn btn--outline btn--sm ${downloadRole === 'field' ? 'active' : ''}`}
                  style={{ borderRadius: 20 }}
                >
                  Field Staff
                </button>
                <button
                  onClick={() => setDownloadRole('supervisor')}
                  className={`btn btn--outline btn--sm ${downloadRole === 'supervisor' ? 'active' : ''}`}
                  style={{ borderRadius: 20 }}
                >
                  Supervisor
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
                <button
                  onClick={() => setDownloadPlatform('android')}
                  className={`btn btn--outline btn--icon-only ${downloadPlatform === 'android' ? 'active' : ''}`}
                  style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 999 }}
                  aria-label="Android"
                  title="Android"
                >
                  <FaAndroid size={18} />
                </button>
                <button
                  onClick={() => setDownloadPlatform('ios')}
                  className={`btn btn--outline btn--icon-only ${downloadPlatform === 'ios' ? 'active' : ''}`}
                  style={{ width: 40, minWidth: 40, height: 40, minHeight: 40, borderRadius: 999 }}
                  aria-label="iOS"
                  title="iOS"
                >
                  <FaApple size={18} />
                </button>
              </div>
              <div style={{ 
                padding: 16, 
                background: '#fff', 
                borderRadius: 12, 
                border: `1px solid ${colors.border}`,
                display: 'inline-block'
              }}>
                <QRCodeSVG 
                  value={getDownloadAppUrl()} 
                  size={140} 
                  imageSettings={{ src: qrLogoSrc, height: 28, width: 28, excavate: true }}
                  fgColor="#1e293b"
                  bgColor="#ffffff"
                />
              </div>
              <div style={{ fontSize: 12, color: colors.text, marginTop: 10, fontWeight: 500 }}>
                {downloadRole === 'field' ? 'Field Staff' : 'Supervisor'} App ({downloadPlatform === 'android' ? 'Android' : 'iOS'})
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                Scan to install from store
              </div>
            </div>

            {/* Onboarding QR */}
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                Select Team
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 16 }}>
                {departments.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDeptIdForQR(String(d.id))}
                    className={`btn btn--outline btn--sm ${String(d.id) === String(selectedDeptIdForQR) ? 'active' : ''}`}
                    style={{ borderRadius: 20 }}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
              <div style={{ 
                padding: 20, 
                background: '#fff', 
                borderRadius: 12, 
                border: `1px solid ${colors.border}`,
                display: 'inline-block'
              }}>
                <QRCodeSVG 
                  value={getOnboardingQrValue()} 
                  size={180} 
                  imageSettings={{ src: qrLogoSrc, height: 32, width: 32, excavate: true }}
                  level="M"
                  fgColor="#1e293b"
                  bgColor="#ffffff"
                />
              </div>
              <div style={{ fontSize: 13, color: colors.text, marginTop: 12, fontWeight: 500 }}>
                Scan to join {departments.find(d => String(d.id) === String(selectedDeptIdForQR))?.name || 'team'}
              </div>
              <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                💡 Keep screen brightness high for faster scanning
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
        <div className="page-content">
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text, fontWeight: 500 }}>Account Settings</h2>
          <div style={{
            marginTop: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textMuted,
            fontSize: 14,
            gap: 16
          }}>
            <div style={{
              width: 44,
              height: 44,
              border: `3px solid ${colors.border}`,
              borderTop: `3px solid ${colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Loading company data...
          </div>
        </div>
      </div>
    );
  }

  // Auth check
  if (!isAuthenticated) {
    return (
      <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
        <div className="page-content">
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text }}>Account Settings</h2>
          <div style={{ color: colors.textMuted, marginTop: 16 }}>Please log in to access your account</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
        <div className="page-content">
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text, fontWeight: 500 }}>Account Settings</h2>
          <div style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.danger}40`,
            borderRadius: 10,
            padding: '12px 16px',
            color: colors.danger,
            fontSize: 13,
            marginTop: 16
          }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  const supervisorActiveCount = supervisors.filter(isMemberActive).length;
  const supervisorInactiveCount = Math.max(0, supervisors.length - supervisorActiveCount);
  const employeeActiveCount = employees.filter(isMemberActive).length;
  const employeeInactiveCount = Math.max(0, employees.length - employeeActiveCount);

  return (
    <div className="page-container" style={{ background: colors.pageBg, minHeight: '100vh' }}>
      <style>{`
        .stat-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .member-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.15s ease;
          border: 1px solid transparent;
        }
        .member-row:hover {
          background: #f1f5f9;
          border-color: #e2e8f0;
        }
        .tab-btn {
          padding: 10px 20px;
          border: none;
          background: transparent;
          color: #64748b;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.15s ease;
        }
        .tab-btn:hover {
          background: rgba(0,0,0,0.03);
          color: #1e293b;
        }
        .tab-btn.active {
          background: #1e293b;
          color: #fff;
        }
        .info-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 16px;
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }
        .dept-card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s ease;
        }
        .dept-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          border-color: #cbd5e1;
        }
      `}</style>

      <div className="page-content">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 20, margin: 0, color: colors.text, fontWeight: 500 }}>Account Settings</h2>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => {
                setSelectedDeptIdForQR(departments[0]?.id ? String(departments[0].id) : '');
                setIsAddUserOpen(true);
              }}
              className="btn btn--primary btn--md btn--icon"
              title="Invite user"
            >
              <FiPlus size={16} />
              Invite User
            </button>
            <div style={{ display: 'flex', gap: 4, background: colors.pageBg, padding: 4, borderRadius: 10 }}>
              <button
                className={`tab-btn ${activeTab === 'organization' ? 'active' : ''}`}
                onClick={() => setActiveTab('organization')}
              >
                Organization
              </button>
              <button
                className={`tab-btn ${activeTab === 'account' ? 'active' : ''}`}
                onClick={() => setActiveTab('account')}
              >
                Company Profile
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primary }}>
              <FiGrid size={20} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 500, color: colors.text }}>{departments.length}</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>Teams</div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: colors.purpleBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.purple }}>
              <FiUserCheck size={20} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 500, color: colors.text }}>{supervisors.length}</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>Supervisors</div>
              <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
                {supervisorActiveCount} active / {supervisorInactiveCount} inactive
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: colors.successBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.success }}>
              <FiUsers size={20} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 500, color: colors.text }}>{employees.length}</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>Field Staff</div>
              <div style={{ fontSize: 10, color: colors.textMuted, marginTop: 2 }}>
                {employeeActiveCount} active / {employeeInactiveCount} inactive
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div style={{ width: 42, height: 42, borderRadius: 10, background: colors.warningBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.warning }}>
              <FiUser size={20} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 500, color: colors.text }}>{supervisors.filter(s => s.isAdmin).length}</div>
              <div style={{ fontSize: 12, color: colors.textMuted }}>Admins</div>
            </div>
          </div>
        </div>

        {/* Company Profile Tab */}
        {activeTab === 'account' && (
          <>
            {/* Company Header Card */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${colors.border}`,
              overflow: 'hidden',
              marginBottom: 16
            }}>
              {/* Cover & Logo Section */}
              <div style={{
                height: 120,
                background: companyDetails?.coverImage
                  ? `url(${companyDetails.coverImage}) center/cover`
                  : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.purple} 100%)`,
                position: 'relative'
              }}>
                {/* Readability overlay for cover images */}
                {companyDetails?.coverImage && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.45) 100%)'
                  }} />
                )}
                <button
                  onClick={() => setIsEditOpen(true)}
                  className="btn btn--secondary btn--icon-only"
                  style={{ position: 'absolute', top: 12, right: 12, boxShadow: 'none' }}
                  aria-label="Edit company"
                  title="Edit company"
                >
                  <FiEdit2 size={16} />
                </button>
                {/* Identity block */}
                <div style={{
                  position: 'absolute',
                  left: 20,
                  bottom: -28,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 14,
                  zIndex: 1
                }}>
                  <div style={{
                    width: 72,
                    height: 72,
                    borderRadius: 14,
                    background: '#fff',
                    border: `3px solid #fff`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {(companyDetails?.logo || company?.logo) ? (
                      <img src={companyDetails?.logo || company?.logo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <FiGrid size={28} color={colors.textMuted} />
                    )}
                  </div>
                </div>
              </div>
              <div style={{ padding: '44px 24px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 500, color: colors.text, lineHeight: 1.2 }}>
                      {companyDetails?.name || company?.name}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {(companyDetails?.isActive !== undefined) && (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 500,
                          background: companyDetails?.isActive ? colors.successBg : colors.dangerBg,
                          color: companyDetails?.isActive ? colors.success : colors.danger
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                          {companyDetails?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>

                    {/* Inline details under status (no separate cards) */}
                    <div style={{
                      marginTop: 12,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: 12
                    }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.primary, flexShrink: 0 }}>
                          <FiPhone size={15} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Phone</div>
                          <div style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>{companyDetails?.phone || '-'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.purpleBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.purple, flexShrink: 0 }}>
                          <FiMail size={15} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Email</div>
                          <div style={{ fontSize: 13, color: colors.text, fontWeight: 500, wordBreak: 'break-word' }}>{companyDetails?.email || '-'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.successBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.success, flexShrink: 0 }}>
                          <FiGlobe size={15} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Website</div>
                          {companyDetails?.website ? (
                            <a href={companyDetails.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: colors.text, fontWeight: 500, textDecoration: 'underline', wordBreak: 'break-word' }}>
                              {companyDetails.website}
                            </a>
                          ) : (
                            <div style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>-</div>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.warningBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.warning, flexShrink: 0 }}>
                          <FiFileText size={15} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>GSTIN</div>
                          <div style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>{companyDetails?.gstin || '-'}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, gridColumn: '1 / -1' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: colors.dangerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.danger, flexShrink: 0 }}>
                          <FiMapPin size={15} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Address</div>
                          <div style={{ fontSize: 13, color: colors.text, fontWeight: 500, lineHeight: 1.5 }}>{formatAddress(companyDetails?.address) || '-'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Profile Card */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${colors.border}`,
              padding: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 12
            }}>
              <div style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.purple} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 18,
                fontWeight: 500,
                flexShrink: 0
              }}>
                {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 2 }}>Your profile</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: colors.text, lineHeight: 1.25 }}>{user?.firstName} {user?.lastName}</div>
                <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2, wordBreak: 'break-word' }}>{user?.email}</div>
                <span style={{
                  display: 'inline-block',
                  padding: '3px 8px',
                  background: user?.role?.toLowerCase?.().includes('admin') ? colors.purpleBg : colors.pageBg,
                  color: user?.role?.toLowerCase?.().includes('admin') ? colors.purple : colors.textMuted,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  marginTop: 6
                }}>
                  {user?.role}
                </span>
              </div>
            </div>
          </>
        )}

        {/* Organization Tab */}
        {activeTab === 'organization' && (
          <>
            {departments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: colors.textMuted }}>
                <FiGrid size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                <div style={{ fontSize: 15, marginBottom: 8, fontWeight: 500 }}>No Teams Created</div>
                <div style={{ fontSize: 13 }}>Your organization structure will appear here once teams are set up.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
                {departments.map((dept, deptIndex) => {
                  const deptAdmins = supervisors.filter(s => s.isAdmin && s.departments?.some(d => d.id === dept.id));
                  const deptSupervisors = supervisors.filter(s => s.isSupervisor && !s.isAdmin && s.departments?.some(d => d.id === dept.id));
                  const deptEmployees = employees.filter(e => e.departments?.some(d => d.id === dept.id));
                  const memberCount = deptAdmins.length + deptSupervisors.length + deptEmployees.length;

                  return (
                    <div key={dept.id} className="dept-card">
                      {/* Department Header */}
                      <div style={{
                        padding: '16px 20px',
                        borderBottom: `1px solid ${colors.border}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: colors.pageBg
                      }}>
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 500, color: colors.text }}>{dept.name}</div>
                          {dept.description && (
                            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>{dept.description}</div>
                          )}
                        </div>
                        <span style={{
                          padding: '5px 12px',
                          background: colors.text,
                          color: '#fff',
                          borderRadius: 20,
                          fontSize: 11,
                          fontWeight: 500
                        }}>
                          {memberCount} members
                        </span>
                      </div>

                      {/* Members */}
                      <div style={{ padding: '12px 8px' }}>
                        {/* Leadership */}
                        {(deptAdmins.length > 0 || deptSupervisors.length > 0) && (
                          <div style={{ marginBottom: 8 }}>
                            <div style={{ fontSize: 10, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 12px', marginBottom: 6 }}>
                              Leadership ({deptAdmins.length + deptSupervisors.length})
                            </div>
                            {[...deptAdmins, ...deptSupervisors].map((member, idx) => {
                              const initials = `${member.firstName?.charAt(0) || ''}${member.lastName?.charAt(0) || ''}`;
                              const globalIdx = deptIndex * 100 + idx;
                              const memberActive = isMemberActive(member);
                              return (
                                <div 
                                  key={member.id} 
                                  className="member-row"
                                  onClick={() => handleMemberClick(member, globalIdx)}
                                >
                                  <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    background: getAvatarColor(globalIdx),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    flexShrink: 0,
                                    overflow: 'hidden'
                                  }}>
                                    {member.profilePicture ? (
                                      <img src={member.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : initials}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{member.firstName} {member.lastName}</div>
                                    <div style={{ fontSize: 11, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>
                                  </div>
                                  <span style={{
                                    padding: '3px 8px',
                                    background: member.isAdmin ? colors.text : colors.purpleBg,
                                    color: member.isAdmin ? '#fff' : colors.purple,
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 500
                                  }}>
                                    {member.isAdmin ? 'Admin' : 'Sup'}
                                  </span>
                                  <span style={{
                                    padding: '3px 8px',
                                    background: memberActive ? colors.successBg : colors.dangerBg,
                                    color: memberActive ? colors.success : colors.danger,
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 500
                                  }}>
                                    {memberActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <FiChevronRight size={14} color={colors.textMuted} />
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Field Staff */}
                        {deptEmployees.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 500, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 12px', marginBottom: 6 }}>
                              Field Staff ({deptEmployees.length})
                            </div>
                            {deptEmployees.map((member, idx) => {
                              const initials = `${member.firstName?.charAt(0) || ''}${member.lastName?.charAt(0) || ''}`;
                              const globalIdx = deptIndex * 100 + 50 + idx;
                              const memberActive = isMemberActive(member);
                              return (
                                <div 
                                  key={member.id} 
                                  className="member-row"
                                  onClick={() => handleMemberClick(member, globalIdx)}
                                >
                                  <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    background: getAvatarColor(globalIdx),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    flexShrink: 0,
                                    overflow: 'hidden'
                                  }}>
                                    {member.profilePicture ? (
                                      <img src={member.profilePicture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : initials}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{member.firstName} {member.lastName}</div>
                                    <div style={{ fontSize: 11, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{member.email}</div>
                                  </div>
                                  <span style={{
                                    padding: '3px 8px',
                                    background: memberActive ? colors.successBg : colors.dangerBg,
                                    color: memberActive ? colors.success : colors.danger,
                                    borderRadius: 4,
                                    fontSize: 10,
                                    fontWeight: 500
                                  }}>
                                    {memberActive ? 'Active' : 'Inactive'}
                                  </span>
                                  <FiChevronRight size={14} color={colors.textMuted} />
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Empty state */}
                        {memberCount === 0 && (
                          <div style={{ textAlign: 'center', padding: 24, color: colors.textMuted }}>
                            <FiUsers size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                            <div style={{ fontSize: 12 }}>No members in this team yet</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {selectedMember && renderMemberModal()}
      {isEditOpen && renderEditModal()}
      {isAddUserOpen && renderInviteModal()}
    </div>
  );
};

export default Account;
